import React, { useEffect, useState } from 'react';
import './ReauthModal.css';

/**
 * ReauthModal Component
 * 
 * Displays a modal prompting merchants to reconnect their app when
 * OAuth access token is invalid or expired.
 * 
 * Props:
 * - show: boolean - Whether to show the modal
 * - shopDomain: string - Shop domain (e.g., 'example.myshopify.com')
 * - errorDetails: string - Optional error message to display
 * - onReconnect: function - Callback when reconnect button is clicked
 * - onDismiss: function - Callback when dismiss is clicked
 * - dismissible: boolean - Whether user can dismiss the modal (default: false for critical errors)
 */
const ReauthModal = ({ 
  show = false, 
  shopDomain = '', 
  errorDetails = null,
  onReconnect = null,
  onDismiss = null,
  dismissible = false
}) => {
  const [isVisible, setIsVisible] = useState(show);

  useEffect(() => {
    setIsVisible(show);
  }, [show]);

  if (!isVisible) return null;

  // Generate reauth URL
  const generateReauthUrl = () => {
    if (!shopDomain) return '#';
    
    // Use the OAuth backend URL (Railway) NOT the API backend (Emergent preview)
    // REACT_APP_OAUTH_BACKEND_URL should point to where /api/shopify/install lives
    const OAUTH_BACKEND_URL = (
      process.env.REACT_APP_OAUTH_BACKEND_URL || 
      process.env.REACT_APP_BACKEND_URL || 
      window.location.origin
    ).replace(/\/$/, '');
    
    // Add embedded=1 to trigger iframe escape on backend
    const url = `${OAUTH_BACKEND_URL}/api/shopify/install?shop=${encodeURIComponent(shopDomain)}&embedded=1`;
    
    
    return url;
  };

  const handleOpenReinstall = () => {
    
    // Generate the OAuth install URL
    const installUrl = generateReauthUrl();
    
    if (!installUrl || installUrl === '#') {
      console.error('❌ Cannot generate install URL - missing shop domain');
      return;
    }
    
    
    // Call custom callback if provided
    if (onReconnect) {
      onReconnect(installUrl);
    }
    
    // Open install link at top level (breaks out of iframe)
    // This is the most reliable method - no App Bridge, no complex redirects
    try {
      if (window.top) {
        window.top.location.href = installUrl;
      } else {
        window.location.href = installUrl;
      }
    } catch (e) {
      // Fallback if window.top is restricted
      console.warn('window.top restricted, using window.location');
      window.location.href = installUrl;
    }
  };

  const handleDismiss = () => {
    if (dismissible && onDismiss) {
      onDismiss();
      setIsVisible(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div className="reauth-modal-backdrop" onClick={dismissible ? handleDismiss : null} />
      
      {/* Modal */}
      <div className="reauth-modal">
        <div className="reauth-modal-content">
          {/* Icon */}
          <div className="reauth-modal-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="#FFA500" strokeWidth="2" fill="#FFF4E5"/>
              <path d="M12 8v4M12 16h.01" stroke="#FFA500" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>

          {/* Title */}
          <h2 className="reauth-modal-title">
            🔐 App Reinstall Required
          </h2>

          {/* Description */}
          <div className="reauth-modal-description">
            <p>
              Your app connection has expired. To continue using Tax & Duty King, please reinstall the app.
            </p>

            {errorDetails && (
              <div className="reauth-modal-error-details">
                <strong>Technical details:</strong> {errorDetails}
              </div>
            )}
            
            <div className="info-box" style={{marginBottom: '20px', padding: '15px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #0ea5e9'}}>
              <p style={{marginBottom: '10px', fontWeight: 'bold'}}>
                ✨ Your data is safe:
              </p>
              <ul style={{marginBottom: 0}}>
                <li>All your settings will be preserved</li>
                <li>Configuration remains intact</li>
                <li>Takes less than 1 minute</li>
              </ul>
            </div>

            <div className="reinstall-steps">
              <p style={{fontWeight: 'bold', marginBottom: '12px'}}>📋 Two ways to reinstall:</p>
              
              <div style={{padding: '15px', backgroundColor: '#f0f9ff', borderRadius: '8px', marginBottom: '15px', border: '1px solid #0ea5e9'}}>
                <p style={{fontWeight: 'bold', marginBottom: '8px', color: '#0369a1'}}>
                  Option 1: Quick Reinstall (Recommended)
                </p>
                <p style={{fontSize: '14px', marginBottom: '10px'}}>
                  Click the button below to open the reinstall link. This will take you through Shopify&apos;s authorization process.
                </p>
                <button 
                  className="reauth-modal-btn-primary"
                  onClick={handleOpenReinstall}
                  style={{width: '100%'}}
                >
                  🚀 Open Reinstall Link
                </button>
              </div>

              <div style={{padding: '15px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb'}}>
                <p style={{fontWeight: 'bold', marginBottom: '8px', color: '#374151'}}>
                  Option 2: Manual Reinstall
                </p>
                <ol style={{fontSize: '14px', lineHeight: '1.8', marginBottom: 0, paddingLeft: '20px'}}>
                  <li>Go to <strong>Apps</strong> in your Shopify Admin</li>
                  <li>Click <strong>Tax & Duty King</strong></li>
                  <li>Click <strong>Uninstall</strong> (don&apos;t worry, your settings are saved!)</li>
                  <li>Reinstall the app from the Shopify App Store</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="reauth-modal-actions">
            {dismissible && (
              <button 
                className="reauth-modal-btn-secondary" 
                onClick={handleDismiss}
                style={{width: '100%'}}
              >
                Remind Me Later
              </button>
            )}
          </div>

          {/* Footer Note */}
          <div className="reauth-modal-footer">
            <p className="reauth-modal-footer-note">
              ℹ️ Your store data and settings will remain unchanged. This only refreshes
              the app connection.
            </p>
          </div>

          {/* Dismiss X button (only if dismissible) */}
          {dismissible && (
            <button 
              className="reauth-modal-close"
              onClick={handleDismiss}
              aria-label="Close"
            >
              ×
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default ReauthModal;
